import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HeartIcon, ChatBubbleLeftIcon, UserGroupIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { Link } from 'react-router-dom';
import Avatar from '../common/Avatar';
import SmartImage from '../common/SmartImage';
import { resolveImageUrl } from '../../utils/image';
import { getUserRoleLabel } from '../../utils/format';
import { Portfolio, Collaboration, User } from '../../types';
import toast from 'react-hot-toast';

// Helper for short time format
const formatShortTimeAgo = (dateStr: string) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d`;
  return `${Math.floor(diffInSeconds / 604800)}w`;
};

interface MemoryBoardCardProps {
  item: Portfolio | Collaboration & { type?: 'portfolio' | 'collaboration' };
  style: { rotation: number; pinStyle: any; offsetY: number };
  isExpanded: boolean;
  currentUser: User | null;
  isFollowing: boolean;
  onToggleExpand: (e: React.MouseEvent) => void;
  onLike: (e: React.MouseEvent) => void;
  onFollow: (item: Portfolio) => void;
  onSelectPortfolio: (item: Portfolio) => void;
  onSelectCollaboration: (item: Collaboration) => void;
  children?: React.ReactNode; // For comments section
}

const MemoryBoardCard: React.FC<MemoryBoardCardProps> = ({
  item,
  style,
  isExpanded,
  currentUser,
  isFollowing,
  onToggleExpand,
  onLike,
  onFollow,
  onSelectPortfolio,
  onSelectCollaboration,
  children
}) => {
  const [imagesLoaded, setImagesLoaded] = useState(0);
  const [totalImages, setTotalImages] = useState(0);
  
  const isCollaboration = 'type' in item && item.type === 'collaboration';
  
  // Determine Primary and Secondary Users
  let primaryUser = isCollaboration ? (item as any).creator : (item as any).user;
  let secondaryUser: any = null;
  let realCollaborators: any[] = [];

  if (isCollaboration) {
      const creator = (item as any).creator;
      const collaborators = (item as any).collaborators || [];
      realCollaborators = collaborators.filter((c: any) => c.user_id !== (item as any).creator_id);

      const followsCreator = (item as any).is_known;
      const followedCollaborator = realCollaborators.find((c: any) => c.is_known);

      if (!followsCreator && followedCollaborator) {
          primaryUser = followedCollaborator.user || { 
              id: followedCollaborator.user_id, 
              first_name: followedCollaborator.name ? followedCollaborator.name.split(' ')[0] : '', 
              last_name: followedCollaborator.name ? followedCollaborator.name.split(' ').slice(1).join(' ') : '',
              profile_pic: followedCollaborator.profile_pic,
              user_type: 'collaborator'
          };
          secondaryUser = creator;
      } else {
          primaryUser = creator;
          if (realCollaborators.length > 0) {
              const firstCollab = realCollaborators[0];
              secondaryUser = firstCollab.user || { 
                  id: firstCollab.user_id, 
                  first_name: firstCollab.name ? firstCollab.name.split(' ')[0] : '', 
                  last_name: firstCollab.name ? firstCollab.name.split(' ').slice(1).join(' ') : '',
                  profile_pic: firstCollab.profile_pic,
                  user_type: 'collaborator',
                  name: firstCollab.name,
                  is_portal_user: firstCollab.is_portal_user
              };
          }
      }
  }

  // Determine images to display
  const displayImages = useMemo(() => {
    let images: string[] = [];
    if (isCollaboration) {
       const collab = item as any;
       if (collab.thumbnail_images_signed && collab.thumbnail_images_signed.length > 0) {
           images = collab.thumbnail_images_signed;
       } else if (collab.medium_images_signed && collab.medium_images_signed.length > 0) {
           images = collab.medium_images_signed;
       } else if (collab.images_signed && collab.images_signed.length > 0) {
           images = collab.images_signed;
       } else if (collab.thumbnail_cover_image_signed) {
           images = [collab.thumbnail_cover_image_signed];
       } else if (collab.medium_cover_image_signed) {
           images = [collab.medium_cover_image_signed];
       } else if (collab.cover_image_signed) {
           images = [collab.cover_image_signed];
       }
    } else {
       const portfolio = item as any;
       images = [portfolio.thumbnail_image_url_signed || portfolio.thumbnail_image_url || portfolio.medium_image_url_signed || portfolio.medium_image_url || portfolio.image_url_signed || portfolio.image_url];
    }
    return images.map((img: string) => resolveImageUrl(img)).filter((img: string) => img !== '');
  }, [item, isCollaboration]);

  // Update total images count when displayImages changes
  React.useEffect(() => {
    setTotalImages(Math.min(displayImages.length, 3));
  }, [displayImages.length]);

  const handleImageLoad = () => {
    setImagesLoaded(prev => prev + 1);
  };

  const isFullyLoaded = imagesLoaded >= totalImages;

  // Skip rendering if primary user is missing, soft deleted, scheduled for deletion, or disabled
  if (!primaryUser || primaryUser.deleted_at || primaryUser.deletion_scheduled_at || primaryUser.is_disabled || (!primaryUser.first_name && !primaryUser.name)) {
    return null;
  }

  return (
    <motion.div
      className="relative"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      style={{
          rotate: style.rotation,
          transformOrigin: 'top center',
          zIndex: isExpanded ? 50 : 1,
          marginTop: style.offsetY 
      }}
    >
        {/* Pin - Realistic */}
        <div 
          className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-20 w-4 h-4 rounded-full shadow-md border border-white/20 dark:border-gray-800/50"
          style={{ 
            background: style.pinStyle.color,
            boxShadow: `0 2px 4px ${style.pinStyle.shadow}, inset 0 1px 2px rgba(255,255,255,0.3)`
          }}
        />

        {/* Skeleton Card Overlay */}
        {!isFullyLoaded && (
             <div className="relative z-20 bg-white dark:bg-gray-800 p-4 pb-5 shadow-md border border-gray-100 dark:border-gray-700 rounded-[2px]">
                {/* Header Skeleton */}
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse" />
                    <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 animate-pulse" />
                        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2 animate-pulse" />
                    </div>
                </div>
                
                {/* Image Skeleton */}
                <div className={`w-full bg-gray-200 dark:bg-gray-700 animate-pulse mb-4 ${
                    displayImages.length === 1 ? 'h-64' : 
                    displayImages.length === 2 ? 'h-48' : 'aspect-[4/5]'
                }`} />
                
                {/* Content Skeleton */}
                <div className="space-y-2 mb-4">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full animate-pulse" />
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6 animate-pulse" />
                </div>
                
                {/* Actions Skeleton */}
                <div className="flex justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex gap-4">
                        <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                        <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                    </div>
                    <div className="w-16 h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                </div>
             </div>
        )}

      <div 
          className={`relative z-10 bg-white dark:bg-gray-800 p-4 pb-5 transition-all duration-300 ${
            isExpanded ? 'scale-105 shadow-2xl' : 'hover:scale-[1.01] hover:shadow-lg'
          } shadow-md border border-gray-100 dark:border-gray-700 ${!isFullyLoaded ? 'opacity-0 absolute inset-0 pointer-events-none' : 'opacity-100'}`}
          style={{ 
              borderRadius: '2px',
          }}  
      >
        {/* User Header */}
        <div className="block">
          <div className="flex items-start gap-3 mb-3 group/user">
              <Link 
                to={`/profile/${primaryUser?.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                  <Avatar 
                    src={resolveImageUrl(primaryUser?.profile_pic_thumb_signed || primaryUser?.profile_pic)} 
                    alt={primaryUser?.first_name}
                    size="md"
                    className="w-10 h-10 ring-2 ring-white dark:ring-gray-700 shadow-sm"
                  />
              </Link>
              <div className="flex flex-col min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1 text-sm font-bold text-gray-800 dark:text-gray-100 transition-colors min-w-0">
                      <Link 
                        to={`/profile/${primaryUser?.id}`}
                        className={`${secondaryUser ? 'flex-shrink-0' : 'truncate'} hover:text-primary-600 dark:hover:text-primary-400`}
                        onClick={(e) => e.stopPropagation()}
                      >
                          {primaryUser?.first_name} {secondaryUser ? '' : primaryUser?.last_name}
                      </Link>
                      
                      {secondaryUser && (
                          <span className="flex items-center min-w-0">
                              <span className="mx-1 flex-shrink-0">&</span>
                              {realCollaborators.length > 1 ? (
                                  <span className="truncate">{realCollaborators.length} others</span>
                              ) : (
                                  secondaryUser.id ? (
                                      <Link 
                                          to={`/profile/${secondaryUser.id}`}
                                          className="hover:text-primary-600 dark:hover:text-primary-400 truncate"
                                          onClick={(e) => e.stopPropagation()}
                                      >
                                          {secondaryUser.first_name || secondaryUser.name?.split(' ')[0]}
                                      </Link>
                                  ) : (
                                      <span 
                                          className="cursor-not-allowed text-gray-600 dark:text-gray-400 truncate"
                                          onClick={(e) => {
                                              e.stopPropagation();
                                              toast('External User', {
                                                  icon: '👤',
                                                  style: {
                                                      borderRadius: '10px',
                                                      background: '#333',
                                                      color: '#fff',
                                                  },
                                              });
                                          }}
                                      >
                                          {secondaryUser.name?.split(' ')[0]}
                                      </span>
                                  )
                              )}
                          </span>
                      )}
                  </div>
                  <span className="text-xs text-gray-400 dark:text-gray-500 font-medium whitespace-nowrap flex-shrink-0">
                    {formatShortTimeAgo(item.created_at)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full w-fit font-medium tracking-wide ${
                      primaryUser?.user_type === 'creator' 
                      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' 
                      : primaryUser?.user_type === 'model'
                      ? 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}>
                      {getUserRoleLabel(primaryUser)}
                  </span>
                  {/* isCollaboration && (
                      <span className="text-[10px] font-bold text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20 px-2 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1">
                          <UserGroupIcon className="w-3 h-3" />
                          Collab
                      </span>
                  ) */}
                </div>
              </div>
          </div>
        </div>

        {/* Image Grid */}
        {displayImages.length > 0 && (
        <div 
          className={`relative overflow-hidden cursor-pointer mb-4 shadow-inner group/image ${!isFullyLoaded ? 'bg-gray-100 dark:bg-gray-900' : ''}`}
          onClick={() => isCollaboration ? onSelectCollaboration(item as any) : onSelectPortfolio(item as any)}
        >
          {(() => {
            if (displayImages.length === 1) {
                return (
                    <div className={`relative rounded-2xl overflow-hidden ${!isFullyLoaded ? 'bg-gray-100 dark:bg-gray-800/50 min-h-[200px] animate-pulse' : ''}`}>
                      <SmartImage
                        src={displayImages[0]}
                        alt={item.title}
                        loading="lazy"
                        className="w-full h-auto object-cover transition-transform duration-700 group-hover/image:scale-105"
                        onLoad={handleImageLoad}
                        onError={handleImageLoad}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover/image:opacity-100 transition-opacity duration-300" />
                    </div>
                );
            } else if (displayImages.length === 2) {
                return (
                    <div className="grid grid-cols-2 gap-0.5 h-48">
                        {displayImages.map((img, idx) => (
                            <div key={idx} className={`relative h-full overflow-hidden bg-gray-100 dark:bg-gray-800/50 ${!isFullyLoaded ? 'animate-pulse' : ''}`}>
                                <SmartImage
                                    src={img}
                                    alt={`${item.title} ${idx + 1}`}
                                    loading="lazy"
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover/image:scale-105"
                                    onLoad={handleImageLoad}
                                    onError={handleImageLoad}
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover/image:bg-black/10 transition-colors duration-300" />
                            </div>
                        ))}
                    </div>
                );
            } else {
                // 3 or more images
                return (
                    <div className="flex flex-col gap-0.5 aspect-[4/5] w-full">
                        {/* Main Image - Top */}
                        <div className={`relative w-full h-[65%] overflow-hidden bg-gray-100 dark:bg-gray-800/50 ${!isFullyLoaded ? 'animate-pulse' : ''}`}>
                            <SmartImage
                                src={displayImages[0]}
                                alt={item.title}
                                loading="lazy"
                                className="w-full h-full object-cover transition-transform duration-700 group-hover/image:scale-105"
                                onLoad={handleImageLoad}
                                onError={handleImageLoad}
                            />
                        </div>
                        {/* Bottom Row */}
                        <div className="flex gap-0.5 w-full h-[35%] overflow-hidden">
                            {/* Bottom Left */}
                            <div className={`relative w-1/2 h-full overflow-hidden bg-gray-100 dark:bg-gray-800/50 ${!isFullyLoaded ? 'animate-pulse' : ''}`}>
                                <SmartImage
                                    src={displayImages[1]}
                                    alt={`${item.title} 2`}
                                    loading="lazy"
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover/image:scale-105"
                                    onLoad={handleImageLoad}
                                    onError={handleImageLoad}
                                />
                            </div>
                            {/* Bottom Right (with count if > 3) */}
                            <div className={`relative w-1/2 h-full overflow-hidden bg-gray-100 dark:bg-gray-800/50 ${!isFullyLoaded ? 'animate-pulse' : ''}`}>
                                <SmartImage
                                    src={displayImages[2]}
                                    alt={`${item.title} 3`}
                                    loading="lazy"
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover/image:scale-105"
                                    onLoad={handleImageLoad}
                                    onError={handleImageLoad}
                                />
                                {displayImages.length > 3 && (
                                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                        <span className="text-white font-bold text-lg">+{displayImages.length - 3}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            }
          })()}
          
          {/* Location Badge for Collabs */}
          {isCollaboration && 'place' in item && item.place && (
              <div className="absolute bottom-2 left-2 bg-black/50 backdrop-blur-sm text-white text-[10px] font-medium px-2 py-1 rounded-full flex items-center gap-1 z-10">
                  <MapPinIcon className="w-3 h-3" />
                  {item.place}
              </div>
          )}
        </div>
        )}

        {/* Content */}
        <div className="px-1">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight mb-2 line-clamp-2 break-words font-serif">
            {item.title}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-3 mb-4 break-words leading-relaxed italic" style={{ fontFamily: '"Georgia", "Times New Roman", serif', fontWeight: 300, letterSpacing: '0.01em' }}>
              "{isCollaboration ? (item as any).project_description : (item as any).description}"
          </p>

          {/* Actions */}
          {!isCollaboration && (
            <div className="flex items-center justify-between pt-3 border-t border-gray-300 dark:border-gray-700 border-dashed">
              <div className="flex gap-4">
                <button
                  onClick={onLike}
                  className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                    (item as any).hasLiked ? 'text-red-600' : 'text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400'
                  }`}
                >
                  {(item as any).hasLiked ? (
                    <HeartIconSolid className="w-5 h-5" />
                  ) : (
                    <HeartIcon className="w-5 h-5" />
                  )}
                  <span className="font-mono text-xs">{(item as any).likes || 0}</span>
                </button>
                
                <button
                  onClick={onToggleExpand}
                  className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                    isExpanded ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'
                  }`}
                >
                  <ChatBubbleLeftIcon className="w-5 h-5" />
                  <span className="font-mono text-xs">{(item as any).comments_count || 0}</span>
                </button>
              </div>

              {/* Follow Button (Contextual) */}
              {!isCollaboration && currentUser && currentUser.id !== (item as any).user?.id && 
               (!((item as any).user?.do_i_follow) || isFollowing) && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onFollow(item as any);
                    }}
                    className={`text-xs font-bold px-3 py-1 rounded-sm border-2 transition-all uppercase tracking-wider ${
                        isFollowing
                        ? 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                        : 'border-primary-200 dark:border-primary-800 text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                    }`}
                >
                    {isFollowing ? 'Following' : '+ Follow'}
                </button>
              )}
            </div>
          )}
          
          {isCollaboration && (
             <div className="pt-3 border-t border-gray-300 dark:border-gray-700 border-dashed flex justify-between items-center">
               <div className="flex -space-x-2 pl-1">
                 {(item as any).collaborators?.slice(0, 4).map((c: any, i: number) => {
                   const isPortalUser = c.is_portal_user && c.user_id && !c.user?.deletion_scheduled_at;
                   return (
                     <div 
                       key={i} 
                       className={`relative transition-transform hover:scale-110 hover:z-10 ${isPortalUser ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                       title={c.name}
                       onClick={(e) => {
                         if (isPortalUser) {
                           e.stopPropagation();
                         } else {
                           e.stopPropagation();
                           toast('External User', {
                              icon: '👤',
                              style: {
                                  borderRadius: '10px',
                                  background: '#333',
                                  color: '#fff',
                              },
                           });
                         }
                       }}
                     >
                       {isPortalUser ? (
                         <Link 
                           to={`/profile/${c.user_id}`}
                           onClick={(e) => e.stopPropagation()}
                         >
                           <Avatar 
                              src={c.user?.profile_pic_signed || c.profile_pic_signed || resolveImageUrl(c.user?.profile_pic || c.profile_pic)} 
                              alt={c.name} 
                              size="sm" 
                              className="border-2 border-white dark:border-gray-800 w-7 h-7 shadow-sm" 
                           />
                         </Link>
                       ) : (
                          <Avatar 
                              src={c.user?.profile_pic_signed || c.profile_pic_signed || resolveImageUrl(c.user?.profile_pic || c.profile_pic)} 
                              alt={c.name} 
                              size="sm" 
                              className="border-2 border-white dark:border-gray-800 w-7 h-7 shadow-sm grayscale opacity-80" 
                          />
                       )}
                     </div>
                   );
                 })}
                 {((item as any).collaborators?.length || 0) > 4 && (
                   <div className="w-7 h-7 rounded-full bg-gray-50 dark:bg-gray-700 border-2 border-white dark:border-gray-800 flex items-center justify-center text-[9px] font-bold text-gray-500 dark:text-gray-400 shadow-sm z-0">
                     +{(item as any).collaborators!.length - 4}
                   </div>
                 )}
               </div>
               <button 
                 onClick={() => onSelectCollaboration(item as any)}
                 className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:underline"
               >
                 View Details
               </button>
             </div>
          )}
        </div>

        {/* Comments Section (Only for Portfolios) */}
        {!isCollaboration && (
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                  {children}
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
    </motion.div>
  );
};

export default MemoryBoardCard;
